import React, { useState, useEffect } from "react";
import { Modal, Input, Button, Select, Form, message, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";

const { Option } = Select;

const UpdateUserModal = ({ visible, onCancel, userId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (visible && userId) {
      fetchUserDetails();
    } else if (!visible) {
      form.resetFields();
      setFileList([]);
    }
  }, [visible, userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/auth/getUserById/${userId}`);
      if (response.status === 200) {
        const user = response.data;

        // Normalize roles (ensure always array)
        const rolesArray = Array.isArray(user.roles)
          ? user.roles
          : user.roles
          ? [user.roles]
          : [];

        //  Pre-fill form with user details
        form.setFieldsValue({
          userName: user.userName || "",
          userId: user.userId || "",
          roles: rolesArray, // 👈 pre-select multiple roles
        });

        //  Show existing signature in Upload preview
        if (user.signatureUrl) {
          setFileList([
            {
              uid: "-1",
              name: "signature.png",
              status: "done",
              url: user.signatureUrl,
            },
          ]);
        }
      } else {
        message.error("Failed to fetch user details.");
      }
    } catch (error) {
      message.error("Error fetching user details.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("userName", values.userName);
    formData.append("userId", values.userId.toLowerCase());
    if (values.password) formData.append("password", values.password);

    //  Append roles as JSON string
    formData.append("roles", JSON.stringify(values.roles || []));

    if (fileList.length > 0 && !fileList[0].url) {
      formData.append("signature", fileList[0].originFileObj);
    }

    try {
      setLoading(true);
      const response = await axios.put(
        `${apiUrl}/api/auth/updateUser/${userId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.success) {
        message.success("User updated successfully!");
        onCancel();
      } else {
        message.error(response.data.message || "Failed to update user.");
      }
    } catch (error) {
      message.error("Some error occurred while updating.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Update User Details"
      open={visible}
      onCancel={onCancel}
      footer={null}
    >
      <Form layout="vertical" onFinish={handleSubmit} form={form}>
        <Form.Item
          label="User Name"
          name="userName"
          rules={[{ required: true, message: "Please input the user name!" }]}
        >
          <Input placeholder="User Name" />
        </Form.Item>

        <Form.Item
          label="User Email"
          name="userId"
          rules={[{ required: true, message: "Please input the user email!" }]}
        >
          <Input placeholder="User Email" />
        </Form.Item>

        <Form.Item label="Password" name="password">
          <Input.Password placeholder="Password (leave blank to keep current)" />
        </Form.Item>

        {/*  MULTI-SELECT WITH PRE-FILLED VALUES */}
        <Form.Item
          label="Roles"
          name="roles"
          rules={[{ required: true, message: "Please select at least one role!" }]}
        >
          <Select
            mode="multiple"
            placeholder="Select one or more roles"
            allowClear
          >
            <Option value="SUPER_ADMIN">Super Admin</Option>
            <Option value="ACCOUNT_ADMIN">Account Admin</Option>
            <Option value="AUDIT_ADMIN">Audit Admin</Option>
            <Option value="AUDITOR">Auditor</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Signature" name="signature">
          <Upload
            beforeUpload={() => false}
            fileList={fileList}
            onChange={({ fileList }) => setFileList(fileList)}
            accept="image/*"
            listType="picture"
          >
            <Button icon={<UploadOutlined />}>Upload Signature</Button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="w-full"
            loading={loading}
          >
            Update User
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateUserModal;
